/**
 * Sentry client initialization.
 * This file MUST be imported before any other app code runs.
 */

import { reactRouterBrowserTracingIntegration } from "@sentry/react/react-router";
import * as Sentry from "@sentry/react";

import {
  SENTRY_CONSOLE_LOG_LEVELS,
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
  SENTRY_RELEASE,
  SENTRY_SAMPLE_RATES,
  getSentryTracePropagationTargets,
  isSentryEnabled,
} from "@/shared/configs/sentry.configs";
import { sentryBeforeSend } from "@/shared/utils/sentryBeforeSend.util";

if (isSentryEnabled()) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    // v11 streams spans by default; "static" keeps the v10 transaction-based traces.
    traceLifecycle: "static",
    // No `dataCollection`: the v11 defaults collect what v10's `sendDefaultPii: true` did.
    integrations: [
      reactRouterBrowserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.consoleLoggingIntegration({ levels: SENTRY_CONSOLE_LOG_LEVELS }),
    ],
    tracesSampleRate: SENTRY_SAMPLE_RATES.TRACES,
    tracePropagationTargets: getSentryTracePropagationTargets(),
    replaysSessionSampleRate: SENTRY_SAMPLE_RATES.REPLAYS_SESSION,
    replaysOnErrorSampleRate: SENTRY_SAMPLE_RATES.REPLAYS_ON_ERROR,
    beforeSend: sentryBeforeSend,
  });
}
else if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.warn(
    "Sentry DSN not configured. Error tracking is disabled. Set VITE_SENTRY_DSN to enable Sentry.",
  );
}

export { Sentry };

export const {
  captureException,
  captureMessage,
  captureReactException,
  startSpan,
  setUser,
  setContext,
  setTag,
  withScope,
  logger,
} = Sentry;
